import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { getSocket } from '../socket';
import { queryKeysForTags } from '../workflowQueryMap';

/** Shape of the `workflow:state-changed` event payload (M2 §12.1). */
interface WorkflowStateChangedEvent {
  resource: string;
  resourceId: string;
  action: string;
  state?: string;
  siteId?: string;
  actorUserId?: string;
  occurredAt: string;
  resourceTags?: string[];
}

/**
 * Subscribes to `workflow:state-changed` on the shared portal socket and
 * invalidates the mapped TanStack Query keys so dashboards + lists update in
 * real time (M2 §12.1). Reuses the single shared socket — no second connection.
 */
export function useRealtimeWorkflow() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const qc = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    const socket = getSocket(accessToken);

    const handleWorkflowEvent = (event?: WorkflowStateChangedEvent) => {
      const keys = queryKeysForTags(event?.resourceTags ?? []);
      for (const queryKey of keys) {
        void qc.invalidateQueries({ queryKey });
      }
    };

    socket.on('workflow:state-changed', handleWorkflowEvent);

    return () => {
      socket.off('workflow:state-changed', handleWorkflowEvent);
    };
  }, [isAuthenticated, accessToken, qc]);
}
